import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { AfterViewInit, Directive, EventEmitter, Input, Output } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { TradableWalletListService } from "../../services/wallet/tradable_wallet_list.service";
import { TradableListService } from "../../services/currency/tradable_list.service";
import { Fiat, FiatWallet, Model } from "@models";
import { Index } from "@models/asset/index/index.model";
import { Currency } from "@models/interfaces/currency";
import { convertTradableToFiat, toCurrencyByFiatId } from "@helpers/tradable-convert";
import { BehaviorSubject, Observable } from "rxjs";
import { AccountService } from "../../../account/services/account.service";
import { UserService } from "@main-services/user.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Directive()
export abstract class TradableTileCoreComponent implements AfterViewInit {
	@Input() public fiatWallets: FiatWallet[] = [];
	@Input() public action: string = "";

	@Input() public set filterType(type: typeof Currency) {
		if (this._filterType !== type) {
			this._filterType = type;
			this.updateViewItems(); // Filter implementation -> only triggered on selection
		}
	}
	public get filterType(): typeof Currency {
		return this._filterType;
	}
	protected _filterType: typeof Currency = Currency;

	@Input() public set tradableId(s: string) {
		if (s !== this._tradableId) {
			this._tradableId = s;
			this.onTradableIdChange(); // Mostly necessary for back-button logic in tradeflows
		}
	}
	public get tradableId(): string {
		return this._tradableId;
	}
	private _tradableId: string = "";

	@Output() public onSelect: EventEmitter<TradableWallet> = new EventEmitter<TradableWallet>();
	@Output() public onRefresh: EventEmitter<void> = new EventEmitter<void>();
	@Output() public tradableIdChange: EventEmitter<string> = new EventEmitter<string>();

	public searchInputValue: string = "";
	protected walletList: BehaviorSubject<TradableWallet[]> = new BehaviorSubject([]);

	public constructor(
		protected accountService: AccountService,
		protected modalService: ModalService,
		protected walletService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected userService: UserService
	) {}

	public ngAfterViewInit(): void {
		this.updateViewItems();
		this.walletService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => setTimeout(() => this.updateViewItems(), 200)); // Needs to wait until tradables have been filtered in trade component before updating the list, bit hacky tbh
		this.tradableListService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => setTimeout(() => this.updateViewItems(), 200)); // Subscriptions to the walletList and tradableList will replace the constant walletUpdates in the setters
	}

	public abstract updateViewItems(): void;
	public abstract getWalletSumFiat(m: Model): number;

	public getWalletList(): Observable<TradableWallet[]> {
		if (!this.walletList.getValue()) {
			this.updateViewItems();
		}
		return this.walletList.asObservable();
	}

	protected onTradableIdChange(): void {
		this.updateViewItems();
	}

	public getAction(): string {
		// Remove flows when infinite scroll is implemented
		if (this.action === "voucher") {
			return "buy";
		}
		return this.action;
	}

	public get fiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	protected calculateWalletSum(amount: number, tradable: Tradable): number {
		if (tradable instanceof Index) {
			return toCurrencyByFiatId(amount, this.fiat);
		}
		return convertTradableToFiat(tradable, amount, this.accountService.getDefaultFiat());
	}
}
